import { useFormikContext } from "formik";
import React from "react";
import styled from "styled-components";
import _ from "lodash";
import { DeclarationOfTruth } from "csv-package";

import spacing from "../../../theme/spacing";

import { ApplicationContext } from "../../../reducers/applicationFrame";
import ConfirmSection from "../../../components/ConfirmSection";

import LongFootnote from "../../../components/LongFootnote";
import PageTitle from "../../../components/PageTitle";
import QuestionsFrame from "../../../components/QuestionsFrame";

import template from "../narrativeTemplate";
import narrativeGenerator from "../../../helpers/narrativeGenerator";
import { FlatApplciationType } from "../../../types";
import RadioGroup from "../../../components/RadioGroup";
import colors from "../../../theme/colors";

const Review = () => {
  const {
    state: { schema, narrativeLimit, narrativeKeys },
    goToStep,
  } = React.useContext(ApplicationContext);
  const { values, setFieldValue } = useFormikContext<FlatApplciationType>();

  let displayNarrative = narrativeGenerator(values, template, schema, true);
  try {
    if (values.Status !== "initiated") {
      displayNarrative = values.Narrative.split("\n")
        .join("<br />")
        .split("\r")
        .join("<br />");
    }
  } catch (e: any) {
    // do nothing
  }

  const shareOption1 = 
    `<p>We may share information about your Family Violence Intervention Order (FVIO) and any family violence you’ve told us about.</p>
    <p>This could include:</p>
    <ul><li>What you or the Police wrote in your FVIO application</li><li>Who is protected by the order</li><li>The conditions of the order</li><li>When a hearing is happening</li></ul>
    <p>We only share this information if it helps to reduce the risk or impact of family violence for you or your family.</p>`;
  const shareOption2 =
    `<p>We will only share your identifying information like your name, contact details, date of birth, and demographic details (for example, your age or culture).</p>
    <p>We won’t share anything that talks about your experience of family violence. For example, we won’t share what you or the Police wrote in your FVIO application.</p>
    <p>We might still share:</p>
    <ul><li>A copy of the FVIO (including the conditions)</li><li>The hearing date</li><li>Who is included on the order</li></ul>
    <p>We may also give your contact details to support services, especially if the respondent (the person the order is about) is taking part in a Court Mandated Counselling Program.</p>`;
  const shareOption3 =
    `<p>We won’t give out any information that could identify you.</p>
    <p>If an ISE asks for information, we will remove (redact) your name and other identifying details. If we can’t remove everything that could identify you, we won’t share the information at all.</p>
    <p><strong>Important:</strong> If we believe there is a serious threat to your safety or the safety of a child, we may still need to share certain information.</p>`;

  return (
    <>
      <PageTitle
        heading="Review your final statement"
        description={
          <>
            The statement below has been automatically prepared from the
            information you have provided, Please review this statement and add
            any final information you believe is important in the area provided
            below it.
          </>
        }
      />

      <Narrative
        dangerouslySetInnerHTML={{
          __html: displayNarrative,
        }}
      />

      {!_.isEmpty(narrativeKeys) &&
        typeof values.NarrativeCharsLeft === "number" &&
        values.NarrativeCharsLeft < 0 && (
          <NarrativeCharsOverWarning
            style={{ color: colors.error }}
            name="NarrativeCharsLeft"
          >
            Incident details when combined are over {narrativeLimit} characters.
            Please review and reduce the size of these descriptions.{" "}
            <span onClick={() => goToStep(3)}>Go to Incident details</span>
          </NarrativeCharsOverWarning>
        )}

      <QuestionsFrame>
        <LongFootnote name="sub_info_oth" charLimit={150} short />
      </QuestionsFrame>
      <ConfirmSection
        heading="Confirm safe contact details"
        goToStepIndex={0}
        config={[
          {
            names: [
              "appl_safe_cont",
              "appl_cont_phone",
              "appl_cont_phone_2",
              "appl_cont_phone_3",
              "appl_cont_phone_vm",
              "appl_cont_safe_times",
              "appl_cont_email",
              "appl_cont_email_2",
              "appl_cont_trst_per",
              "appl_cont_trst_per_rel",
              "appl_cont_trst_per_rel_other",
              "appl_cont_trst_per_phone",
              "appl_cont_trst_per_email",
            ],
          },
        ]}
      />
      <QuestionsFrame>
        <h3>Family Violence Information Sharing Scheme</h3>
        <p>
          The Magistrates Court may legally share relevant
          information with external organisations to assess your
          risk or manage your ongoing safety. This is through the
          Family Violence Information Sharing Scheme. Some of
          these organisations include Child Protection,
          Corrections, Victoria Police, schools, and other
          community services. These are known as Information
          Sharing entities (ISE's).
        </p>
        <p style={{ fontSize: "15px" }}>
          Your information may be shared without your consent if
          there is a serious threat to yours or another
          individual's life, health, safety, or welfare. Your
          information may be shared without consent if it is
          relevant to assessing or managing risks to a child.
        </p>
        <p>
          You can find out more information{" "}
          <a
            href="https://www.mcv.vic.gov.au/privacy"
            target="_blank"
            rel="noreferrer"
            title="Privacy"
            style={{ color: colors.blue }}
          >
            here
          </a>
          .
        </p>
        <RadioGroup
          name="appl_info_share"
          label="Select one of the following"
          descriptionView="infobox"
          options={[
            {
              value: "full",
              text: "**I consent** to the sharing of all relevant personal information to ISEs",
              description: shareOption1
            },
            {
              value: "limited",
              text: "**I consent to my contact, date of birth and demographic details** being provided to ISEs",
              description: shareOption2
            },
            {
              value: "no",
              text: "**I do not consent** to the sharing of personal information to ISEs",
              description: shareOption3
            },
          ]}
        />
        <DeclarationOfTruth
          applicantName={`${values.appl_det_giv_nam} ${values.appl_det_fam_nam}`}
          applicantDOB={values.appl_det_dob + ""}
          onCheckboxChange={() =>
            setFieldValue(
              "declaration_of_truth",
              !Boolean(values.declaration_of_truth)
            )
          }
          checked={Boolean(values.declaration_of_truth)}
        />
        {/* <ExtendOrderReminder jurisdiction="FVIO" /> */}
      </QuestionsFrame>
    </>
  );
};

export default Review;

const Narrative = styled.div`
  padding: 0 ${spacing.gutters}px 0;

  h3: {
    font-size: 1.375rem;
    line-height: 1.36;
    margin: 2em 0 1em;
  }

  h3:first-child {
    margin-top: 0;
  }
`;

const NarrativeCharsOverWarning = styled.fieldset`
  background: #f8d9dd;
  color: ${colors.error};
  padding: 15px;
  border: none;
  margin-bottom: 2rem;

  span {
    text-decoration: underline;
    cursor: pointer;
    :hover {
      text-decoration: none;
    }
  }
`;
